import { useCallback } from 'react';

import { Result, err } from 'neverthrow';

import { LoadBuildingGuardrailCustomer } from 'contexts/loadBuildingGuardrail';
import { useLoadBuildingGuardrail } from 'hooks/useLoadBuildingGuardrail';
import { CreateLoadResponse } from 'lib/api/createLoad';
import { createLoadMercuryGateBoomi } from 'lib/api/createLoadMercuryGateBoomi';
import {
  GUARDED_CREATE_LOAD_ERROR_MESSAGE,
  resolveGuardrailCustomer,
  shouldAbortGuardedCreateLoad,
} from 'lib/loadBuilding/guardrail';
import { CreateLoadMGBRequest } from 'pages/QuoteView/LoadBuilding/MercuryGateBoomiSectionForms/buildRequestBody';
import { Customer, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';

type BuildRequest = (opts: {
  acknowledgeProdTMSOverride?: boolean;
}) => CreateLoadMGBRequest;

/**
 * Wraps MercuryGate-Boomi create-load with prod-TMS guardrail dialog handling.
 */
export function useGuardedCreateLoadMercuryGateBoomi() {
  const { requestSubmit } = useLoadBuildingGuardrail();

  return useCallback(
    async (
      customer:
        Customer | TMSCustomer | LoadBuildingGuardrailCustomer | undefined,
      buildRequest: BuildRequest,
      tmsCustomers?: Maybe<TMSCustomer[]>
    ): Promise<Result<CreateLoadResponse, ApiError> | null> => {
      let result: Result<CreateLoadResponse, ApiError> | undefined;
      try {
        await requestSubmit(
          resolveGuardrailCustomer(customer, tmsCustomers),
          async (opts) => {
            result = await createLoadMercuryGateBoomi(
              buildRequest({
                acknowledgeProdTMSOverride: opts.acknowledgeProdTMSOverride,
              })
            );
          }
        );
      } catch (error) {
        if (shouldAbortGuardedCreateLoad(error)) {
          return null;
        }
        return err({ message: GUARDED_CREATE_LOAD_ERROR_MESSAGE });
      }
      return result ?? null;
    },
    [requestSubmit]
  );
}
